import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import Tooltip from 'react-tooltip-lite';
import { formatNumericValue } from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { IReconciliation } from '@/modules/workingDayReconciliation/types';
import { Button, Typography } from '@/shared/components/atoms';

import InfoCircle from '~/images/shared/ExleminationMark.svg';
import Eye from '~/images/shared/Eye.svg';

const columnHelper =
  createColumnHelper<IReconciliation.IGetWorkdayBonusesTable>();

export const tableColumns = (
  t: TFunction<'common', undefined>,
  type: IReconciliation.IModalType,
  handleAttachmentClick: (url: string | null) => void,
  handleEditButton: (rowData: IReconciliation.IGetWorkdayBonusesTable) => void,
  handleCancelButton: (id: number, type: IReconciliation.IExpenseType) => void,
  currency: string,
  issuedPayment?: number | null
) => [
  columnHelper.accessor('type', {
    cell: ({ getValue }) => (
      <Typography
        variant='subtitle2'
        className='overflow-clip text-ellipsis whitespace-nowrap text-center'
      >
        {t(getValue().toString().toLowerCase())}
      </Typography>
    ),
    header: () => `${t('type')} ${t(type || '')}`,
  }),
  columnHelper.accessor('incidentReason', {
    cell: ({ getValue }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        <Typography
          variant='subtitle2'
          className=' overflow-clip text-ellipsis whitespace-nowrap '
        >
          {getValue() || '-'}
        </Typography>
      </div>
    ),
    header: () => `${t('reason')} ${t(String(type))}`,
    enableHiding: type === 'bonus',
  }),
  columnHelper.accessor('jobInstanceId', {
    cell: ({ getValue, row }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        <Typography
          variant='subtitle2'
          className=' overflow-clip text-ellipsis whitespace-nowrap '
        >
          {getValue()} - {row.original.jobName}
        </Typography>
      </div>
    ),
    header: () => t('the-job-order'),
  }),
  columnHelper.accessor('cropName', {
    cell: ({ getValue }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        <Typography
          variant='subtitle2'
          className=' overflow-clip text-ellipsis whitespace-nowrap '
        >
          {getValue()}
        </Typography>

        {/* {row?.original?.cropName && (
          <Typography
            variant='subtitle2'
            className=' overflow-clip text-ellipsis whitespace-nowrap '
          >
            {row?.original?.cropName}
          </Typography>
        )} */}
      </div>
    ),
    header: () => t('crop-type'),
  }),
  columnHelper.accessor('memberName', {
    cell: ({ getValue, row }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        <Typography
          variant='subtitle2'
          className=' overflow-clip text-ellipsis whitespace-nowrap '
        >
          {row.original.type === 'Supervision' ? (
            <>
              {getValue()}
              <br />({t('individual')})
            </>
          ) : (
            t('team_leader')
          )}
        </Typography>
      </div>
    ),
    header: () => t('applied-to'),
  }),
  columnHelper.accessor('createdByName', {
    cell: ({
      row: {
        original: { jobTitle, createdByName },
      },
    }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        <Typography
          variant='subtitle2'
          className=' overflow-clip text-ellipsis whitespace-nowrap '
        >
          {createdByName ? (
            <>
              {createdByName}
              {!jobTitle ?? (
                <>
                  <br />({jobTitle})
                </>
              )}
            </>
          ) : (
            '-'
          )}
        </Typography>
      </div>
    ),
    header: () => t('added-by'),
  }),
  columnHelper.accessor('measurementUnit', {
    cell: ({
      getValue,
      row: {
        original: { fractionOfUnits, pricingMethod },
      },
    }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        <Typography
          variant='subtitle2'
          className=' flex items-center gap-6 overflow-clip text-ellipsis whitespace-nowrap'
        >
          {formatNumericValue(Number(fractionOfUnits))}
          {pricingMethod === 0 ? t('daily') : getValue()}
        </Typography>
      </div>
    ),
    header: () => t('number-pay-units'),
  }),
  columnHelper.accessor('amount', {
    cell: ({ getValue, row }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        <Typography
          variant='subtitle2'
          className=' flex items-center gap-6 overflow-clip text-ellipsis whitespace-nowrap'
        >
          {getValue() !== 0 ? (
            <>
              <span>
                {formatNumericValue(getValue())} {currency}
              </span>
              {row.original.deductionType === 1 ||
                (row.original.bonusType === 1 && (
                  <Tooltip
                    background={themeColors().background[200]}
                    className='react-tooltip-rounded'
                    arrow
                    arrowSize={10}
                    content={
                      <div className='flex w-fit min-w-[100px] flex-col px-16 py-8'>
                        {t(`expense-added-as-unit`, {
                          type: t(type ?? ''),
                        })}
                      </div>
                    }
                    direction='bottom'
                    tagName='div'
                    tipContentHover
                    padding={0}
                  >
                    <span className='cursor-pointer text-neutralVariant-80'>
                      <InfoCircle width='16' height='16' />
                    </span>
                  </Tooltip>
                ))}
              {row.original.deductionType === 2 && (
                <Tooltip
                  background={themeColors().background[200]}
                  className='react-tooltip-rounded'
                  arrow
                  arrowSize={10}
                  content={
                    <div className='flex w-fit min-w-[100px] flex-col px-16 py-8'>
                      {t('deduction-value-per-unit')}
                    </div>
                  }
                  direction='bottom'
                  tagName='div'
                  tipContentHover
                  padding={0}
                >
                  <span className='cursor-pointer text-neutralVariant-80'>
                    <InfoCircle width='16' height='16' />
                  </span>
                </Tooltip>
              )}
            </>
          ) : (
            '-'
          )}
        </Typography>
      </div>
    ),
    header: () => t('value'),
  }),
  columnHelper.accessor('attachment', {
    cell: ({ getValue }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        {getValue() ? (
          <Typography
            variant='subtitle2'
            className=' flex items-center gap-2 overflow-clip text-ellipsis whitespace-nowrap'
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleAttachmentClick(getValue());
              }}
              className='cursor-pointer'
            >
              <Eye />
            </span>
          </Typography>
        ) : (
          '-'
        )}
      </div>
    ),
    header: () => t('attachments'),
  }),
  columnHelper.accessor('note', {
    cell: ({ getValue }) => (
      <div className='flex w-full items-center justify-center gap-5'>
        {getValue() && getValue() !== 'undefined' ? (
          <Tooltip
            arrow={false}
            content={
              <div className='max-w-md rounded-lg bg-background-200 px-16 py-2'>
                {getValue()}
              </div>
            }
            direction='center'
            tagName='div'
            tipContentHover
          >
            <Typography
              variant='subtitle2'
              className='max-w-[100px] overflow-clip text-ellipsis whitespace-nowrap'
            >
              {getValue()}
            </Typography>
          </Tooltip>
        ) : (
          '-'
        )}
      </div>
    ),
    header: () => t('note'),
  }),
  columnHelper.accessor('isDeleted', {
    cell: ({ getValue, row }) =>
      !getValue() ? (
        <div className='flex justify-end gap-4'>
          {row.original.bonusType !== 2 && row.original.deductionType !== 2 && (
            <Button
              disabled={Boolean(issuedPayment)}
              id={`open_edit_${type}_${row?.original?.id}`}
              onClick={() => handleEditButton(row?.original)}
              color='primary'
              variant='filled'
              size='sm'
              className='min-w-[84px] max-w-[84px]'
            >
              {t('edit')}
            </Button>
          )}
          <Button
            disabled={Boolean(issuedPayment)}
            id={`open_remove_${type}_${row?.original?.id}`}
            onClick={() =>
              handleCancelButton(row?.original?.id, row?.original?.type)
            }
            size='sm'
            color='red'
            variant='text'
            className='min-w-[84px] max-w-[84px] border-sm'
          >
            {t('cancel')}
          </Button>
        </div>
      ) : (
        <div
          className='flex justify-end'
          id={`cancel_btn_${row?.original?.id}`}
        >
          <Button
            disabled
            type='button'
            size='sm'
            className='min-w-[84px] max-w-[84px]'
          >
            {t('canceled')}
          </Button>
        </div>
      ),
    header: () => '',
  }),
];
